/**
 * DidNumberList — 조직에 할당된 DID(전화번호) 목록 표시 공통 컴포넌트.
 *
 * 사용처:
 *  - Header.tsx Popover (compact=true) — 칩 가로 배열, overflow "외 N개"
 *  - ConsultationModePage.tsx (compact=false) — Card 안 grid
 *  - SubscriptionPage.tsx (compact=false) — 기존 Card 블록 대체
 *
 * 데이터: `GET /api/subscriptions/did-numbers` (ORG_USER+ 권한 ✅)
 * 캐시: TanStack Query 60초 — 자주 안 바뀌는 데이터.
 *
 * 빈 목록·로딩·에러 상태 모두 처리. 호출자는 단순 렌더만.
 */

import { useQuery } from "@tanstack/react-query";
import { Phone } from "lucide-react";
import { subscriptionApi, type DidNumberInfo } from "@/api/subscription";
import { formatPhoneNumber } from "@/utils/phoneFormatter";

interface Props {
  /**
   * 표시 모드 — false=페이지용 full grid, true=헤더용 칩 가로 + overflow.
   * default: false
   */
  compact?: boolean;
  /** 헤더 칩에서 최대 노출 개수 (compact=true에만 의미). default: 1 */
  compactMaxVisible?: number;
}

export default function DidNumberList({
  compact = false,
  compactMaxVisible = 1,
}: Props) {
  const { data, isLoading, isError } = useQuery<DidNumberInfo[]>({
    queryKey: ["did-numbers"],
    queryFn: () =>
      subscriptionApi.getDidNumbers().catch(() => [] as DidNumberInfo[]),
    staleTime: 60_000,
  });

  const dids = data ?? [];

  if (isLoading) {
    return compact ? (
      <span className="text-xs text-muted-foreground">로딩...</span>
    ) : (
      <div className="h-8 bg-muted rounded animate-pulse" />
    );
  }

  if (isError || dids.length === 0) {
    return (
      <span className="text-xs text-muted-foreground italic">
        할당된 번호 없음
      </span>
    );
  }

  if (compact) {
    const visible = dids.slice(0, compactMaxVisible);
    const overflow = dids.length - visible.length;
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {visible.map((d) => (
          <span
            key={d.did}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted/60 font-mono text-xs"
          >
            <Phone className="h-3 w-3 text-muted-foreground" aria-hidden />
            {formatPhoneNumber(d.did)}
          </span>
        ))}
        {overflow > 0 && (
          <span className="text-xs text-muted-foreground">
            외 {overflow}개
          </span>
        )}
      </div>
    );
  }

  // Full grid (페이지용)
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
      {dids.map((d) => (
        <div
          key={d.did}
          className="flex items-center p-2 rounded-md border"
        >
          <Phone className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
          <span className="font-mono text-sm">{formatPhoneNumber(d.did)}</span>
        </div>
      ))}
    </div>
  );
}
